import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Disposable} from '../../../utilities/Disposable';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {FileUploadService} from '../../../services/file-upload.service';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Validators} from '../../../utilities/Validators';
import {
    AssistanceApplicationFileTypeOptions,
    AssistanceApplicationFileTypeOptionsTranslated,
    AssistanceApplicationStlWaterFileTypeOptionsTranslated, AssistanceApplicationStormFileTypeOptionsTranslated,
    AssistanceApplicationUffFileTypeOptionsTranslated,
    AssistanceApplicationUhonsFileTypeOptionsTranslated
} from '../../../shared/constants/assistance-application-file-type-options';
import {AssistanceApplicationFileType} from '../../../shared/enums/assistance-application-file-type';
import {AssistanceApplicationFile, AssistanceApplicationType} from '../../../services/api.client';
import {environment} from '../../../../environments/environment';
import {SystemService} from '../../../services/SystemService';
import {getUtilityLabelFromAssistanceApplicationAreasOfNeed} from '../../../shared/utils/utilty-label-utils';
import swal from 'sweetalert2';
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'app-assistance-application-document-upload',
    templateUrl: './assistance-application-document-upload.component.html',
    styleUrls: ['./assistance-application-document-upload.component.scss'],
    standalone: false
})
export class AssistanceApplicationDocumentUploadComponent extends Disposable implements OnInit {

    get photoIdStatus(): boolean {
        return this.fileUploadService.photoIdTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get employmentStatus(): boolean {
        return this.fileUploadService.employmentTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get incomeStatus(): boolean {
        return this.fileUploadService.incomeTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get residenceStatus(): boolean {
        return this.fileUploadService.proofOfResidenceTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get stlWaterIncomeStatus(): boolean {
        return this.fileUploadService.stlWaterIncomeTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get utilityBillStatus(): boolean {
        return this.fileUploadService.utilityBillTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get childcareBillStatus(): boolean {
        return this.fileUploadService.childcareTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get evictionStatus(): boolean {
        return this.fileUploadService.evictionTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get leaseAgreementStatus(): boolean {
        return this.fileUploadService.leaseAgreementTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get landlordOwnershipStatus(): boolean {
        return this.fileUploadService.landlordOwnershipTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get rentLandlordFormStatus(): boolean {
        return this.fileUploadService.rentLandlordFormTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get delinquentRentNoticeStatus(): boolean {
        return this.fileUploadService.delinquentRentNoticeTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get carRepairEstimateStatus(): boolean {
        return this.fileUploadService.carRepairTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get proofOfIncome(): boolean {
        return this.fileUploadService.proofOfIncomeTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get mortgageStatus(): boolean {
        return this.fileUploadService.mortgageTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get movingExpenseStatus(): boolean {
        return this.fileUploadService.movingExpenseTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get carPaymentStatus(): boolean {
        return this.fileUploadService.carPaymentTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get carInsuranceStatus(): boolean {
        return this.fileUploadService.carInsuranceTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get carOwnershipStatus(): boolean {
        return this.fileUploadService.carOwnershipTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get tuitionStatus(): boolean {
        return this.fileUploadService.tuitionTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get childEnrollmentStatus(): boolean {
        return this.fileUploadService.childEnrollmentTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get childProgressReportStatus(): boolean {
        return this.fileUploadService.childEducationProgressTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get schoolSuppliesStatus(): boolean {
        return this.fileUploadService.schoolSuppliesTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get tutoringStatus(): boolean {
        return this.fileUploadService.tutoringTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get ssnProofStatus(): boolean {
        return this.fileUploadService.ssnProofTypes.some(t => this.hasFileTypeWithFile(t));
    }

    get delinquentWaterBillStatus(): boolean {
        return this.fileUploadService.stlWaterDelinquentWaterBill.some(t => this.hasFileTypeWithFile(t));
    }

    constructor(
        private fileUploadService: FileUploadService,
        private fb: FormBuilder,
        public system: SystemService,
        public translate: TranslateService
    ) {
        super();
    }

    get requestingUtilityAssistance(): boolean {
        return this.areasOfNeedForm.get('uhonsElectric')?.value ||
            this.areasOfNeedForm.get('uhonsGasHeat')?.value ||
            this.areasOfNeedForm.get('uhonsWater')?.value ||
            this.areasOfNeedForm.get('uhonsSewer')?.value ||
            this.areasOfNeedForm.get('uffUtilityBills')?.value;
    }

    get requestingChildcareAssistance(): boolean {
        return this.areasOfNeedForm.get('uhonsChildcareExpenses')?.value ||
            this.areasOfNeedForm.get('uffStateLicensedChildcareProvider')?.value;
    }

    get requestingCarRepairAssistance(): boolean {
        return this.areasOfNeedForm.get('uhonsCarRepair')?.value ||
            this.areasOfNeedForm.get('uffCarRepair')?.value;
    }

    get requestingCarPaymentAssistance(): boolean {
        return this.areasOfNeedForm.get('uffCarPayment')?.value;
    }

    get requestingCarInsuranceAssistance(): boolean {
        return this.areasOfNeedForm.get('uffCarInsurance')?.value;
    }

    get requestingEvictionAssistance(): boolean {
        return this.areasOfNeedForm.get('evictionNotification')?.value;
    }

    get requestingRentAssistance(): boolean {
        return this.areasOfNeedForm.get('uhonsRent')?.value || this.areasOfNeedForm.get('uffRentPayment')?.value;
    }

    get requestingMortgageAssistance(): boolean {
        return this.areasOfNeedForm.get('uffMortgagePayment')?.value;
    }

    get requestingSecurityDepositAssistance(): boolean {
        return this.areasOfNeedForm.get('uffSecurityDeposit')?.value;
    }

    get requestingMovingExpenseAssistance(): boolean {
        return this.areasOfNeedForm.get('uffMovingExpenses')?.value;
    }

    get requestingTuitionAssistance(): boolean {
        return this.areasOfNeedForm.get('uffTuition')?.value;
    }

    get requestingSchoolSuppliesAssistance(): boolean {
        return this.areasOfNeedForm.get('uffChildEdSupportUniforms')?.value;
    }

    get requestingTutoringAssistance(): boolean {
        return this.areasOfNeedForm.get('uffChildSupportTutoring')?.value;
    }

    get requestingStlWaterAssistance(): boolean {
        return this.areasOfNeedForm.get('stlWaterWater')?.value;
    }

    /* -----------------------------
     *  STORM / TORNADO RESPONSE
     *  Requesting flags
     *  ----------------------------- */
    get requestingStormAppliances(): boolean { return this.areasOfNeedForm.get('stormAppliances')?.value; }
    get requestingStormConstruction(): boolean { return this.areasOfNeedForm.get('stormConstruction')?.value; }
    get requestingStormHvacPlumbing(): boolean { return this.areasOfNeedForm.get('stormHvacPlumbing')?.value; }
    get requestingStormMoldRemediationAssistance(): boolean { return this.areasOfNeedForm.get('stormMoldRemediationAssistance')?.value; }
    get requestingStormOtherDisasterCausedUnmetNeeds(): boolean
    { return this.areasOfNeedForm.get('stormOtherDisasterCausedUnmetNeeds')?.value; }
    get requestingStormTemporaryStorageAssistance(): boolean { return this.areasOfNeedForm.get('stormTemporaryStorageAssistance')?.value; }

    get hasAnyStormAreasSelected(): boolean {
        return this.requestingStormAppliances ||
            this.requestingStormConstruction ||
            this.requestingStormHvacPlumbing ||
            this.requestingStormMoldRemediationAssistance ||
            this.requestingStormOtherDisasterCausedUnmetNeeds ||
            this.requestingStormTemporaryStorageAssistance;
    }

// Appliances — Invoice or Estimate
    get stormAppliancesStatus(): boolean {
        return this.fileUploadService.appliancesTypes?.some(t => this.hasFileTypeWithFile(t));
    }

// Construction — Invoice or Estimate
    get stormConstructionStatus(): boolean {
        return this.fileUploadService.constructionTypes?.some(t => this.hasFileTypeWithFile(t));
    }

// HVAC / Plumbing — Invoice or Estimate
    get stormHvacPlumbingStatus(): boolean {
        return this.fileUploadService.hvacPlumbingTypes?.some(t => this.hasFileTypeWithFile(t));
    }

// Mold Remediation — Invoice (licensed provider)
    get stormMoldRemediationStatus(): boolean {
        return this.fileUploadService.moldRemediationTypes?.some(t => this.hasFileTypeWithFile(t));
    }

// Other Disaster-Caused Unmet Needs — Invoice or Estimate
    get stormOtherUnmetNeedsStatus(): boolean {
        return this.fileUploadService.otherUnmetNeedsTypes?.some(t => this.hasFileTypeWithFile(t));
    }

// Temporary Storage — Statement or Estimate
    get stormStorageStatus(): boolean {
        return this.fileUploadService.storageTypes?.some(t => this.hasFileTypeWithFile(t));
    }

    @Input() formArray: FormArray;
    @Input() applicationType: AssistanceApplicationType;
    @Input() areasOfNeedForm: FormGroup;
    @Input() baseForm: FormGroup;
    @Input() receivedForms: AssistanceApplicationFile[] = [];
    @Input() defaultOpenDocuments = true;
    @Input() shouldBeDisabled = false;
    @Input() isPublic = false;
    @Output() onDeleteFile = new EventEmitter<string[]>();

    private softDeletedFileIds: string[] = [];

    public docOptions = [];
    public showDocumentInformation = false;
    public apiUrl = environment.appConfig.apiBaseUrl;
    public utilityBillLabel: string;
    showUploadedDocuments = true;

    protected readonly AssistanceApplicationType = AssistanceApplicationType;


    private hasFileTypeWithFile(type: AssistanceApplicationFileType): boolean {
        return this.formArray.controls.some(c => {
            const fg = c as FormGroup;
            return fg.get('fileType')?.value === type && !!fg.get('file')?.value;
        });
    }

    private refreshDocOptions(): void {

        switch (this.applicationType) {
            case AssistanceApplicationType.UHONS:
                this.docOptions = AssistanceApplicationUhonsFileTypeOptionsTranslated.map(option => ({
                    label: this.translate.instant(option.labelKey),
                    value: option.value
                }));
                break;
            case AssistanceApplicationType.UFF:
                this.docOptions = AssistanceApplicationUffFileTypeOptionsTranslated.map(option => ({
                    label: this.translate.instant(option.labelKey),
                    value: option.value
                }));
                break;
            case AssistanceApplicationType.STLWater:
                this.docOptions = AssistanceApplicationStlWaterFileTypeOptionsTranslated.map(option => ({
                    label: this.translate.instant(option.labelKey),
                    value: option.value
                }));
                break;
            case AssistanceApplicationType.STLStorm:
                this.docOptions = AssistanceApplicationStormFileTypeOptionsTranslated.map(option => ({
                    label: this.translate.instant(option.labelKey),
                    value: option.value
                }));
                break;
            default:
                this.docOptions = AssistanceApplicationFileTypeOptionsTranslated.map(option => ({
                    label: this.translate.instant(option.labelKey),
                    value: option.value
                }));
        }
    }

    ngOnInit(): void {

        this.refreshDocOptions();

        this.translate.onLangChange
            .pipe(takeUntil(this.$destroyed), distinctUntilChanged())
            .subscribe(() => {
            this.refreshDocOptions();
        });

        this.updateUtilityBillLabelOnUtilityAssistanceRequestChange();
        this.showUploadedDocuments = this.defaultOpenDocuments;
    }

    /* helpers -------------------------------------------------------------- */
    getFileInputFormGroup(i: number): FormGroup {
        return this.formArray.at(i) as FormGroup;
    }

    isFileInputFormValid(i: number): boolean {
        const control = this.getFileInputFormGroup(i);
        return control.valid || control.disabled;
    }

    addDocumentFormGroup(): void {
        this.addFileFormGroup();
    }

    removeDoc(i: number): void {
        const id = this.formArray.at(i)?.value?.id;
        if (id) {
            this.softDeletedFileIds.push(id);
            this.onDeleteFile.emit(this.softDeletedFileIds);
        }
        if (i > -1) { this.formArray.removeAt(i); }
    }

    onFileChange(evt: any, fg: FormGroup): void {
        const f = evt.target.files[0];
        if (f?.size > 50 * 1024 * 1024) {
            swal.fire({heightAuto: false, icon: 'error', title: 'File size limit', text: 'This file exceeds 50 MB.'}).then();
            return;
        }
        fg.get('file')?.setValue(f);
    }

    addFileFormGroup(type?: AssistanceApplicationFileType): void {
        this.formArray.push(
            this.fb.group({fileType: [type, Validators.required], file: [null, Validators.required]})
        );
    }


    /* misc ---------------------------------------------------------------- */
    getFileType(v: AssistanceApplicationFileType): string {
        return AssistanceApplicationFileTypeOptions.find(o => o.value === v)?.label;
    }

    private updateUtilityBillLabelOnUtilityAssistanceRequestChange(): void {
        this.utilityBillLabel = 'Utility Bill';
        this.areasOfNeedForm.valueChanges.subscribe(v => {
            const label = getUtilityLabelFromAssistanceApplicationAreasOfNeed(v);
            this.utilityBillLabel = label;
            this.docOptions = this.docOptions.map(o =>
                o.value === AssistanceApplicationFileType.UtilityBill ? {...o, label} : o
            );
        });
    }
}
